'use client';

import { c as _c } from "react/compiler-runtime";
import type { ReactNode } from 'react';
import { useIsMounted } from 'shared/hooks/use-is-mounted';
import useMediaQuery from 'shared/hooks/use-media-query';
type ViewportSwitchBreakpoint = 'tablet' | 'desktop';

// `contents` keeps the wrapper out of the layout, so the gated markup sits in
// its parent's flex/grid flow exactly as the un-wrapped branch does after mount.
const belowClassName: Record<ViewportSwitchBreakpoint, string> = {
  tablet: 'contents tablet:hidden',
  desktop: 'contents desktop:hidden'
};
const fromClassName: Record<ViewportSwitchBreakpoint, string> = {
  tablet: 'contents max-tablet:hidden',
  desktop: 'contents max-desktop:hidden'
};
interface ViewportSwitchProps {
  /** Breakpoint the two branches switch at. */
  at: ViewportSwitchBreakpoint;
  /** Rendered below `at`. */
  below: ReactNode;
  /** Rendered from `at` up. */
  from: ReactNode;
}

/**
 * Picks one of two markup branches by viewport, without the first-paint flash.
 *
 * `useMediaQuery` seeds its breakpoints on the server from the request
 * User-Agent, so a desktop browser in a narrow window is served desktop-shaped
 * markup that the browser paints before hydration corrects it. Until mount both
 * branches are rendered and CSS — which knows the real viewport — shows one; the
 * losing branch is dropped on the first committed layout effect.
 *
 * Only worth reaching for when the two branches are structurally different
 * markup on the first paint. A branch that differs by a prop, a class, or that
 * is only visible after an interaction does not flash.
 */
export function ViewportSwitch(t0) {
  const $ = _c(11);
  const {
    at,
    below,
    from
  } = t0;
  const isMounted = useIsMounted();
  const {
    isDesktop,
    isMobile
  } = useMediaQuery();
  if (isMounted) {
    const matchesFrom = at === "desktop" ? isDesktop : !isMobile;
    const t1 = matchesFrom ? from : below;
    let t2;
    if ($[0] !== t1) {
      t2 = <>{t1}</>;
      $[0] = t1;
      $[1] = t2;
    } else {
      t2 = $[1];
    }
    return t2;
  }
  const t1 = belowClassName[at];
  let t2;
  if ($[2] !== below || $[3] !== t1) {
    t2 = <div className={t1}>{below}</div>;
    $[2] = below;
    $[3] = t1;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const t3 = fromClassName[at];
  let t4;
  if ($[5] !== from || $[6] !== t3) {
    t4 = <div className={t3}>{from}</div>;
    $[5] = from;
    $[6] = t3;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] !== t2 || $[9] !== t4) {
    t5 = <>{t2}{t4}</>;
    $[8] = t2;
    $[9] = t4;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  return t5;
}